import { Divider, Paper, Stack, Typography } from "@mui/material";

export type Stat = {
  label: string;
  /**
   * Strings are allowed so a strip can carry a formatted quantity ("300 lbs")
   * beside a plain count. Emphasis is then the caller's call rather than being
   * inferred from the number.
   */
  value: number | string;
  /**
   * Highlight this figure. For a numeric value it only takes effect when the
   * number is non-zero, so "0 over threshold" stays quiet; for a string the
   * caller decides outright.
   */
  emphasize?: boolean;
  /** Which semantic to highlight with. Over-threshold is a problem; a manifest
   * difference is something to look at. */
  tone?: "error" | "warning";
};

/**
 * One outlined strip of stat groups; groups are separated by vertical dividers
 * while stats within a group sit flush together. Scrolls horizontally on narrow
 * screens rather than wrapping past the dividers.
 *
 * Lifted out of the accumulation-areas list so the waste pages share one strip
 * instead of each growing its own. Behaviour there is unchanged.
 */
export function StatStrip({ groups }: { groups: Stat[][] }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        gap: 3,
        flexWrap: "wrap",
        // Matches the inset of the section cards these strips sit above, so a
        // stat and the heading below it start on the same line. The vertical
        // stays tight -- the strip is a summary, not a section.
        px: 3,
        py: 1.5,
        mb: 2.5,
        overflowX: "auto",
      }}
    >
      {groups.map((group, groupIndex) => (
        <Stack key={group.map((stat) => stat.label).join("-")} direction="row" sx={{ gap: 3 }}>
          {groupIndex > 0 && <Divider orientation="vertical" flexItem />}
          {group.map((stat) => (
            <Stack key={stat.label} sx={{ minWidth: 88 }}>
              <Typography
                component="span"
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1,
                  color: highlighted(stat) ? `${stat.tone ?? "error"}.main` : "text.primary",
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                sx={{ mt: 0.75, fontWeight: 600, color: "text.secondary" }}
              >
                {stat.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ))}
    </Paper>
  );
}

function highlighted(stat: Stat): boolean {
  if (!stat.emphasize) {
    return false;
  }
  return typeof stat.value === "number" ? stat.value > 0 : true;
}
